/**
 * The API client.
 *
 * Two properties drive its whole shape:
 *
 * 1. The access token lives in a module variable, never in localStorage. The
 *    dashboard renders tenant-authored template previews, so XSS is a live risk
 *    rather than a theoretical one, and localStorage would hand an attacker a
 *    usable session. A module variable dies with the tab; the refresh cookie is
 *    HttpOnly and the script cannot read it at all.
 *
 * 2. Refresh is transparent and single-flight. A dashboard fires several
 *    requests at once, so an expired token would otherwise produce N concurrent
 *    refreshes — and because refresh ROTATES the token, the second one would
 *    present an already-used token, which the server treats as theft and
 *    responds to by killing every session. The inFlightRefresh promise is what
 *    stops the dashboard logging itself out.
 */

import { ProjectHeader, type ApiError } from './types';

let accessToken: string | null = null;
let currentProjectId: string | null = null;
let inFlightRefresh: Promise<boolean> | null = null;

/** Called when refresh fails, so the router can send the user to /login. */
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setCurrentProject(projectId: string | null) {
  currentProjectId = projectId;
  if (projectId) {
    // Persisted so a reload lands on the same project. Only an id, and the
    // server checks it against the caller's memberships on every request — so a
    // tampered value returns 404, not someone else's data.
    localStorage.setItem('seidhi.project', projectId);
  } else {
    localStorage.removeItem('seidhi.project');
  }
}

export function getCurrentProject() {
  return currentProjectId ?? localStorage.getItem('seidhi.project');
}

export function setAuthLostHandler(fn: (() => void) | null) {
  onAuthLost = fn;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown';
    this.details = body?.error?.details;
  }

  /**
   * Field-level messages for a 422, so a form can highlight the offending input
   * instead of showing one generic banner.
   */
  fieldErrors(): Record<string, string> {
    if (!this.details) return {};
    return Object.fromEntries(
      Object.entries(this.details).map(([k, v]) => [k, String(v)]),
    );
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skips the project header, for endpoints that are session- but not project-scoped. */
  noProject?: boolean;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function rawRequest(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  if (!opts.noProject) {
    const project = getCurrentProject();
    if (project) headers[ProjectHeader] = project;
  }

  return fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    // Sends the refresh cookie on /v1/auth/*, where it is scoped.
    credentials: 'same-origin',
    signal: opts.signal,
  });
}

/**
 * Exchanges the refresh cookie for a new access token, at most once at a time.
 */
async function refresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        accessToken = null;
        return false;
      }
      const body = (await res.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers all see the same promise
      // before it is discarded.
      queueMicrotask(() => {
        inFlightRefresh = null;
      });
    }
  })();

  return inFlightRefresh;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await rawRequest(path, opts);

  // One transparent retry on 401. Only one: if the refreshed token is also
  // rejected, retrying again is a loop.
  if (res.status === 401 && !path.startsWith('/v1/auth/')) {
    const refreshed = await refresh();
    if (refreshed) {
      res = await rawRequest(path, opts);
    } else {
      onAuthLost?.();
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    if (res.status === 401) onAuthLost?.();
    throw new ApiRequestError(res.status, body as ApiError, res.statusText);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, opts: RequestOptions = {}) => request<T>(path, opts),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

/**
 * Bootstraps a session on page load by attempting a refresh.
 *
 * Called before the router renders, so a reload with a live cookie lands on the
 * dashboard rather than flashing the login screen first.
 */
export async function restoreSession(): Promise<boolean> {
  return refresh();
}

export async function login(email: string, password: string) {
  const res = await fetch('/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
    credentials: 'same-origin',
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiRequestError(res.status, body, 'sign in failed');
  accessToken = body.access_token;
  return body;
}

export async function register(input: {
  email: string;
  password: string;
  name: string;
  org_name: string;
}) {
  const res = await fetch('/v1/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'same-origin',
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiRequestError(res.status, body, 'registration failed');
  accessToken = body.access_token;
  return body;
}

export async function logout() {
  try {
    await fetch('/v1/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } finally {
    accessToken = null;
    setCurrentProject(null);
  }
}
